import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, TableBody, TableCell, TableHead, TableRow,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Grid, 
  IconButton, Chip, Switch, FormControlLabel, MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import axios from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'react-toastify';
import ScrollTable, { EMBEDDED_MAX_HEIGHT } from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

const emptyForm = {
  type: 'individual',
  name: '',
  primaryContactPhone: '',
  primaryContactEmail: '',
  secondaryContactPhone: '',
  secondaryContactEmail: '',
  addressStreet: '',
  addressCountry: 'Uganda',
  notes: '',
  managementFeePercentage: 0,
  paymentDate: 1,
  // '' = not agreed. The server stores it as NULL and refuses security deposits for this
  // landlord's tenants until one of the two is chosen.
  securityHeldBy: '',
  isActive: true
};

const LandlordManagement = () => {
  const { formatMoney } = useCurrency();
  const [landlords, setLandlords] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, filterValues, setFilter, reset,
    rows: visibleLandlords, resultCount, totalCount
  } = useTableSearch(landlords, {
    searchFields: ['name', 'primaryContactPhone', 'primaryContactEmail', 'addressStreet'],
    filters: {
      type: (row, v) => row.type === v,
      isActive: (row, v) => String(row.isActive !== false) === v
    }
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedLandlord, setSelectedLandlord] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);

  useEffect(() => { fetchLandlords(); }, []);

  const fetchLandlords = async () => {
    try {
      const res = await axios.get('/landlords');
      setLandlords(res.data);
    } catch (error) {
      toast.error('Error fetching landlords');
    }
  };

  const fetchProperties = async (landlordId) => {
    setLoadingProps(true);
    try {
      const res = await axios.get(`/landlords/${landlordId}/properties`);
      setProperties(res.data);
    } catch (error) {
      toast.error('Error fetching properties');
    }
    setLoadingProps(false);
  };

  const handleOpen = (landlord = null) => {
    if (landlord) {
      setEditing(landlord._id);
      setForm({
        type: landlord.type || 'individual',
        name: landlord.name || '',
        primaryContactPhone: landlord.primaryContactPhone || '',
        primaryContactEmail: landlord.primaryContactEmail || '',
        secondaryContactPhone: landlord.secondaryContactPhone || '',
        secondaryContactEmail: landlord.secondaryContactEmail || '',
        addressStreet: landlord.addressStreet || '',
        addressCountry: landlord.addressCountry || 'Uganda',
        notes: landlord.notes || '',
        managementFeePercentage: landlord.managementFeePercentage || 0,
        paymentDate: landlord.paymentDate || 1,
        securityHeldBy: landlord.securityHeldBy || '',
        isActive: landlord.isActive !== undefined ? landlord.isActive : true
      });
    } else {
      setEditing(null);
      setForm(emptyForm);
    }
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!form.name) return toast.error('Name is required');

      if (editing) {
        await axios.put(`/landlords/${editing}`, form);
        toast.success('Landlord updated successfully');
      } else {
        await axios.post('/landlords', form);
        toast.success('Landlord created successfully');
      }
      setOpen(false);
      fetchLandlords();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save landlord');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this landlord? This action cannot be undone.')) return;
    try {
      await axios.delete(`/landlords/${id}`);
      toast.success('Landlord deleted successfully');
      fetchLandlords();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete landlord');
    }
  };

  const handleToggleActive = async (landlord) => {
    const newStatus = !landlord.isActive;
    const action = newStatus ? 'reactivate' : 'deactivate';
    
    if (!window.confirm(
      `Are you sure you want to ${action} this landlord? ${
        !newStatus 
          ? 'This will unassign all tenants from their properties and deactivate tenant accounts.' 
          : 'This will make their properties available again and reactivate tenant accounts.'
      }`
    )) return;

    try {
      await axios.put(`/landlords/${landlord._id}`, {
        ...landlord,
        isActive: newStatus
      });
      
      if (newStatus) {
        toast.success('Landlord reactivated successfully. Properties are now available and tenant accounts have been reactivated.');
      } else {
        toast.success('Landlord deactivated successfully. All tenants have been unassigned and their accounts deactivated.');
      }
      fetchLandlords();
      if (selectedLandlord && selectedLandlord._id === landlord._id) {
        fetchProperties(landlord._id);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${action} landlord`);
    }
  };

  const handleDashboard = (landlord) => {
    setSelectedLandlord(landlord);
    fetchProperties(landlord._id);
  };

  const handleBack = () => {
    setSelectedLandlord(null);
    setProperties([]);
  };

  // Dashboard metrics
  const summary = () => {
    const count = properties.length;
    const rentRoll = properties.reduce((a, p) => a + (p.monthlyRent || 0), 0);
    const occupied = properties.filter(p => p.status === 'occupied').length;
    const unavailable = properties.filter(p => p.status === 'unavailable').length;
    return { count, rentRoll, occupied, available: count - occupied - unavailable, unavailable };
  };

  const s = summary();

  return (
    <Paper sx={{ p: 3 }}>
      {!selectedLandlord ? (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">Landlord Management</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
              Add Landlord
            </Button>
          </Box>
      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Name, phone or email"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
        filters={[
          { key: 'type', label: 'Type', value: filterValues.type || '',
            onChange: (v) => setFilter('type', v),
            options: [{ value: 'individual', label: 'Individual' }, { value: 'company', label: 'Company' }] },
          { key: 'isActive', label: 'Status', value: filterValues.isActive || '',
            onChange: (v) => setFilter('isActive', v), allLabel: 'All statuses',
            options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }] }
        ]}
      />
          <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT}>
              <TableHead>
                <TableRow>
                  <TableCell className="pin-left">Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Primary Contact</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Properties</TableCell>
                  <TableCell className="pin-right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleLandlords.map((landlord) => (
                  <TableRow key={landlord._id}>
                    <TableCell className="pin-left">{landlord.name}</TableCell>
                    <TableCell>
                      {landlord.type === 'company' ? (
                        <BusinessIcon color="primary" />
                      ) : (
                        <PersonIcon color="primary" />
                      )}
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        {landlord.type === 'company' ? 'Company' : 'Individual'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{landlord.primaryContactPhone}</Typography>
                        {landlord.primaryContactEmail && (
                          <Typography variant="caption" color="text.secondary">
                            {landlord.primaryContactEmail}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {landlord.addressStreet}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={landlord.isActive ? 'Active' : 'Inactive'}
                        color={landlord.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        color="info"
                        size="small"
                        onClick={() => handleDashboard(landlord)}
                      >
                        View Properties
                      </Button>
                    </TableCell>
                    <TableCell className="pin-right">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={landlord.isActive}
                            onChange={() => handleToggleActive(landlord)}
                            size="small"
                          />
                        }
                        label=""
                        sx={{ mr: 1 }}
                      />
                      <IconButton onClick={() => handleOpen(landlord)} size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(landlord._id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ScrollTable>

          <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>{editing ? 'Edit' : 'Add'} Landlord</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12}>
                  <TextField
                    select
                    label="Type"
                    fullWidth
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value="individual">Individual</option>
                    <option value="company">Company</option>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Name"
                    fullWidth
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Primary Contact
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Primary Phone"
                    fullWidth
                    value={form.primaryContactPhone}
                    onChange={(e) => setForm({ ...form, primaryContactPhone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Primary Email"
                    fullWidth
                    type="email"
                    value={form.primaryContactEmail}
                    onChange={(e) => setForm({ ...form, primaryContactEmail: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Secondary Contact (Optional)
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Secondary Phone"
                    fullWidth
                    value={form.secondaryContactPhone}
                    onChange={(e) => setForm({ ...form, secondaryContactPhone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Secondary Email"
                    fullWidth
                    type="email"
                    value={form.secondaryContactEmail}
                    onChange={(e) => setForm({ ...form, secondaryContactEmail: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Address
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Street"
                    fullWidth
                    value={form.addressStreet}
                    onChange={(e) => setForm({ ...form, addressStreet: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Country"
                    fullWidth
                    value={form.addressCountry}
                    onChange={(e) => setForm({ ...form, addressCountry: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    fullWidth
                    multiline
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Management Fee Percentage"
                    fullWidth
                    required
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    value={form.managementFeePercentage || 0}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setForm({ ...form, managementFeePercentage: parseFloat(e.target.value) || 0 })}
                    helperText="% of rent charged as management fee"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Default Pay Day (Day of Month)"
                    fullWidth
                    required
                    type="number"
                    inputProps={{ min: 1, max: 31, step: 1 }}
                    value={form.paymentDate || 1}
                    onChange={(e) => {
                      const val = Math.min(31, Math.max(1, parseInt(e.target.value) || 1));
                      setForm({ ...form, paymentDate: val });
                    }}
                    helperText="Applied to new properties for this landlord. Each property carries its own pay day, set on the property, and that is what governs payment."
                  />
                </Grid>
                <Grid item xs={12}>
                  {/* Agreed per landlord, for all their properties. Each deposit copies the
                      choice when it is received, so changing this affects deposits taken
                      from now on and never one already held. */}
                  <TextField
                    select
                    fullWidth
                    label="Security deposits held by"
                    value={form.securityHeldBy || ''}
                    onChange={(e) => setForm({ ...form, securityHeldBy: e.target.value })}
                    helperText={
                      form.securityHeldBy === 'agency'
                        ? 'The agency keeps tenants’ deposits and refunds them itself at move-out.'
                        : form.securityHeldBy === 'landlord'
                          ? 'Deposits are paid over to the landlord (commission is earned on them); refunds are deducted from the landlord’s pay.'
                          : 'Not agreed yet — security deposits cannot be recorded for this landlord’s tenants until this is set.'
                    }
                  >
                    <MenuItem value="">Not agreed</MenuItem>
                    <MenuItem value="agency">Agency</MenuItem>
                    <MenuItem value="landlord">Landlord</MenuItem>
                  </TextField>
                </Grid>
                {editing && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.isActive}
                          onChange={(e) =>
                            setForm({ ...form, isActive: e.target.checked })
                          }
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmit} variant="contained">
                {editing ? 'Update' : 'Add'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <>
          <Box mb={2} display="flex" alignItems="center" gap={2}>
            <Button onClick={handleBack} variant="outlined">
              Back to Landlords
            </Button>
            <Typography variant="h6">
              Portfolio Dashboard for <b>{selectedLandlord.name}</b>
            </Typography>
            <Chip
              label={selectedLandlord.isActive ? 'Active' : 'Inactive'}
              color={selectedLandlord.isActive ? 'success' : 'default'}
              size="small"
            />
          </Box>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Paper sx={{ p: 2, bgcolor: '#1976d2', color: '#fff', minHeight: 90 }}>
                <Typography>Total Properties</Typography>
                <Typography variant="h5">{s.count}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Paper sx={{ p: 2, bgcolor: '#7b1fa2', color: '#fff', minHeight: 90 }}>
                <Typography>Rent Roll</Typography>
                <Typography variant="h5">{formatMoney(s.rentRoll, { decimals: 0 })}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Paper sx={{ p: 2, bgcolor: '#388e3c', color: '#fff', minHeight: 90 }}>
                <Typography>Occupied</Typography>
                <Typography variant="h5">{s.occupied}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Paper sx={{ p: 2, bgcolor: '#ffd600', color: '#222', minHeight: 90 }}>
                <Typography>Available</Typography>
                <Typography variant="h5">{s.available}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Paper sx={{ p: 2, bgcolor: '#d32f2f', color: '#fff', minHeight: 90 }}>
                <Typography>Unavailable</Typography>
                <Typography variant="h5">{s.unavailable}</Typography>
              </Paper>
            </Grid>
          </Grid>
          <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>
            Properties
          </Typography>
          <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rent</TableCell>
                  <TableCell>Occupancy</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingProps && (
                  <TableRow>
                    <TableCell colSpan={5}>Loading...</TableCell>
                  </TableRow>
                )}
                {properties.map((p) => (
                  <TableRow key={p._id}>
                    <TableCell>{p.propertyName}</TableCell>
                    <TableCell>{p.propertyType}</TableCell>
                    <TableCell>
                      <Chip
                        label={p.status}
                        color={
                          p.status === 'occupied'
                            ? 'success'
                            : p.status === 'available'
                            ? 'primary'
                            : p.status === 'unavailable'
                            ? 'error'
                            : 'warning'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatMoney(p.monthlyRent, { decimals: 0 })}</TableCell>
                    <TableCell>
                      {p.propertyType === 'commercial'
                        ? `${p.allocationsCount || 0} allocation(s)`
                        : `${p.occupiedUnitsCount || 0}/${p.unitsCount || 0} occupied`}
                    </TableCell>
                  </TableRow>
                ))}
                {!loadingProps && properties.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>No properties found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </ScrollTable>
        </>
      )}
    </Paper>
  );
};

export default LandlordManagement;

